import { useEffect, useState } from 'react';
import { useAuth, useUser } from '@clerk/react';

interface AdminState {
  isAdmin: boolean;
  loading: boolean;
}

/**
 * Reads the `role` claim from the baldydev Clerk JWT template.
 * Returns { isAdmin, loading } — loading is true while Clerk is initialising
 * or while the token is being fetched.
 */
export function useIsAdmin(): AdminState {
  const { isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [state, setState] = useState<AdminState>({ isAdmin: false, loading: true });

  useEffect(() => {
    if (!isLoaded) return; // Clerk still bootstrapping

    if (!isSignedIn) {
      setState({ isAdmin: false, loading: false });
      return;
    }

    getToken({ template: 'baldydev' }).then((token: string | null) => {
      if (!token) {
        setState({ isAdmin: false, loading: false });
        return;
      }
      try {
        // The JWT payload is the middle segment, base64-encoded.
        const payload = JSON.parse(atob(token.split('.')[1]));
        setState({ isAdmin: payload.role === 'admin', loading: false });
      } catch {
        setState({ isAdmin: false, loading: false });
      }
    });
    // getToken is stable across renders per Clerk docs — omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, isLoaded]);

  return state;
}
